// ProofImagePickerChip — compact, single-button picker for card layouts.
//
// ════════════════════════════════════════════════════════════════════
// WHY THIS EXISTS
// ════════════════════════════════════════════════════════════════════
//
// The default <ProofImagePicker> renders a label + button + hint stack
// designed for modal forms where there's plenty of horizontal room.
// In narrow card columns (the agent / admin queue cards' top-right
// "right column", which is `shrink-0` so it never squeezes), that
// stack expands the column to ~160 px and squeezes the content column
// to <100 px on a 360-px phone — the date and username wrap one word
// per line and the card looks broken.
//
// This chip renders ONLY a small pill button ("+ Upload proof") that
// hides a native <input type="file"> behind it. Same validation
// pipeline (mime / size / canvas laundering) as the full picker —
// just no preview / label / hint, so it fits next to a FlowChip.
//
// Use this inside table-row / card-column contexts. Use the full
// <ProofImagePicker> inside modals and dedicated upload forms.
//
// ════════════════════════════════════════════════════════════════════
// PROPS
// ════════════════════════════════════════════════════════════════════
//
//   onFile      (blob: Blob) => void | Promise<void>
//                Called after the user picks a valid image AND the
//                canvas re-encode succeeds. Awaited if it returns a
//                promise so the chip stays in "Uploading…" state until
//                the parent's upload call resolves.
//   onError     (msg: string) => void  Optional. Receives validation
//                or re-encode error messages.
//   disabled    boolean   Greys out the button.
//   label       string    Default: "+ Upload proof".

import { useRef, useState } from 'react';
import {
  reencodeProofImageViaCanvas,
  PROOF_PICKER_MAX_BYTES,
  PROOF_PICKER_ALLOWED_MIME,
} from './ProofImagePicker';

type Props = {
  onFile: (blob: Blob) => void | Promise<void>;
  onError?: (msg: string) => void;
  disabled?: boolean;
  label?: string;
};

export default function ProofImagePickerChip({
  onFile, onError, disabled = false, label = '+ Upload proof',
}: Props) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  async function pick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';                    // allow re-picking the same file
    if (!file) return;
    if (!PROOF_PICKER_ALLOWED_MIME.has(file.type)) {
      onError?.('Only JPG, PNG, or WebP images are allowed.');
      return;
    }
    if (file.size > PROOF_PICKER_MAX_BYTES) {
      onError?.('File too large — max 5 MB.');
      return;
    }
    setBusy(true);
    try {
      const cleaned = await reencodeProofImageViaCanvas(file);
      await onFile(cleaned);
    } catch (err) {
      onError?.((err as Error).message || 'Could not process image');
    } finally {
      setBusy(false);
    }
  }

  return (
    <label className={`inline-flex items-center justify-center gap-1 px-2.5 py-1 rounded-full text-[11.5px] font-semibold ring-1 ring-inset transition cursor-pointer whitespace-nowrap
        ${disabled || busy
          ? 'bg-arena-100/70 text-arena-500 ring-arena-200/50 cursor-not-allowed dark:bg-arena-700/40 dark:text-neon-200/55 dark:ring-arena-600/40'
          : 'bg-amber-50 text-amber-800 ring-amber-200/60 hover:bg-amber-100 dark:bg-amber-500/15 dark:text-amber-200 dark:ring-amber-400/30 dark:hover:bg-amber-500/25'}`}>
      <span>{busy ? 'Uploading…' : label}</span>
      <input type="file" accept="image/jpeg,image/png,image/webp"
             ref={inputRef} onChange={pick}
             disabled={disabled || busy} className="hidden" />
    </label>
  );
}
